/**
 * Every write this package sends (2.D deliverable 3): one hook each over `api`, in this module
 * only. Each refreshes the queries it changed. A refusal comes back as fixed copy: the caller
 * shows it beside its control, or the hook passes it to `useNotify` where the caller has no place
 * for it. 2.L adds `undoable(route)` here.
 */
import {
  type SessionAttendancePutV1,
  type SessionCreateV1,
  type SessionItemCreateV1,
  type SessionItemMoveV1,
  type SessionItemUpdateV1,
  type SessionItemV1,
  type SessionStartV1,
  type SessionV1,
  sessionsAttendancePut,
  sessionsCreate,
  sessionsItemsCreate,
  sessionsItemsMove,
  sessionsItemsRemove,
  sessionsItemsUpdate,
  sessionsStart,
  sessionsUpdate,
} from '@codex/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { ApiError, api } from '../auth/client.js';
import { loginRedirectUrl } from '../auth/require-session.js';
import { useNotify } from '../shell/notices.js';
import { forgetMe, isSigningOut } from '../shell/session.js';
import { undoable } from '../undo/undoable.js';
import { noticeFor } from './core/messages.js';
import { itemsKey, TONIGHT_KEY } from './queries.js';

export type WriteOutcome<T> =
  | { readonly ok: true; readonly data: T }
  | { readonly ok: false; readonly code: string | null; readonly message: string };

function issueOf(error: ApiError): string | undefined {
  const issues = (error.body.details as { issues?: { code?: unknown }[] } | undefined)?.issues;
  const code = issues?.[0]?.code;
  return typeof code === 'string' ? code : undefined;
}

/** The fixed copy for a failure; a 401 ends the session the way the shell's query cache does. */
export function failure(error: unknown, action: 'start' | 'write'): WriteOutcome<never> {
  if (error instanceof ApiError) {
    if (error.status === 401 && !isSigningOut()) {
      forgetMe();
      window.location.assign(loginRedirectUrl(window.location.pathname));
    }
    return {
      ok: false,
      code: error.code,
      message: noticeFor(error.code, { action, issue: issueOf(error) }),
    };
  }
  return { ok: false, code: null, message: noticeFor(null) };
}

/** `sessions.create`: a planned session with its title; Start assigns the number. */
export function useCreateSession() {
  const client = useQueryClient();
  const mutation = useMutation({
    mutationFn: (body: SessionCreateV1) =>
      api(sessionsCreate, { body }, undoable('sessions.create')),
  });
  const run = useCallback(
    async (body: SessionCreateV1): Promise<WriteOutcome<SessionV1>> => {
      try {
        const data = await mutation.mutateAsync(body);
        await client.invalidateQueries({ queryKey: TONIGHT_KEY });
        return { ok: true, data };
      } catch (error) {
        return failure(error, 'write');
      }
    },
    [client, mutation.mutateAsync],
  );
  return { run, pending: mutation.isPending };
}

/** `sessions.start`: the number in the field, once. */
export function useStartSession() {
  const client = useQueryClient();
  const mutation = useMutation({
    mutationFn: (v: { sessionId: string; body: SessionStartV1 }) =>
      api(sessionsStart, { params: { id: v.sessionId }, body: v.body }),
  });
  const run = useCallback(
    async (sessionId: string, body: SessionStartV1): Promise<WriteOutcome<SessionV1>> => {
      try {
        const data = await mutation.mutateAsync({ sessionId, body });
        await client.invalidateQueries({ queryKey: TONIGHT_KEY });
        return { ok: true, data };
      } catch (error) {
        return failure(error, 'start');
      }
    },
    [client, mutation.mutateAsync],
  );
  return { run, pending: mutation.isPending };
}

/** `sessions.attendance.put`: the whole set; a refusal is a notice and the caller restores the boxes. */
export function usePutAttendance(sessionId: string | undefined) {
  const client = useQueryClient();
  const notify = useNotify();
  const mutation = useMutation({
    mutationFn: (body: SessionAttendancePutV1) =>
      api(
        sessionsAttendancePut,
        { params: { id: sessionId as string }, body },
        undoable('sessions.attendance.put'),
      ),
  });
  return useCallback(
    async (body: SessionAttendancePutV1): Promise<boolean> => {
      if (sessionId === undefined) return false;
      try {
        await mutation.mutateAsync(body);
        await client.invalidateQueries({ queryKey: TONIGHT_KEY });
        return true;
      } catch (error) {
        const out = failure(error, 'write');
        if (!out.ok) notify(out.message);
        await client.invalidateQueries({ queryKey: TONIGHT_KEY });
        return false;
      }
    },
    [client, notify, sessionId, mutation.mutateAsync],
  );
}

/**
 * The run-sheet item writes (stage s2): `sessions.items.create`, `.update`, `.move` and `.remove`.
 * Each refreshes the session's items and Tonight's answer. A refusal comes back as an outcome;
 * `notify` says whether the hook also shows it (a form that keeps its text shows it itself).
 */
export function useItemWrites(sessionId: string | undefined) {
  const client = useQueryClient();
  const notify = useNotify();
  const create = useMutation({
    mutationFn: (body: SessionItemCreateV1) =>
      api(
        sessionsItemsCreate,
        { params: { id: sessionId as string }, body },
        undoable('sessions.items.create'),
      ),
  });
  const update = useMutation({
    mutationFn: (v: { itemId: string; body: SessionItemUpdateV1 }) =>
      api(
        sessionsItemsUpdate,
        { params: { id: sessionId as string, itemId: v.itemId }, body: v.body },
        undoable('sessions.items.update'),
      ),
  });
  const move = useMutation({
    mutationFn: (v: { itemId: string; body: SessionItemMoveV1 }) =>
      api(
        sessionsItemsMove,
        { params: { id: sessionId as string, itemId: v.itemId }, body: v.body },
        undoable('sessions.items.move'),
      ),
  });
  const remove = useMutation({
    mutationFn: (v: { itemId: string; baseRv: number }) =>
      api(
        sessionsItemsRemove,
        { params: { id: sessionId as string, itemId: v.itemId }, query: { baseRv: v.baseRv } },
        undoable('sessions.items.remove'),
      ),
  });

  const refresh = useCallback(async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: itemsKey(sessionId) }),
      client.invalidateQueries({ queryKey: TONIGHT_KEY }),
    ]);
  }, [client, sessionId]);

  const settle = useCallback(
    async <T>(run: () => Promise<T>, quiet: boolean): Promise<WriteOutcome<T>> => {
      if (sessionId === undefined) return { ok: false, code: null, message: noticeFor(null) };
      try {
        const data = await run();
        await refresh();
        return { ok: true, data };
      } catch (error) {
        const out = failure(error, 'write');
        if (!out.ok && !quiet) notify(out.message);
        await refresh();
        return out as WriteOutcome<never>;
      }
    },
    [notify, refresh, sessionId],
  );

  const createItem = useCallback(
    (body: SessionItemCreateV1): Promise<WriteOutcome<SessionItemV1>> =>
      settle(() => create.mutateAsync(body), false),
    [create.mutateAsync, settle],
  );
  const updateItem = useCallback(
    (itemId: string, body: SessionItemUpdateV1): Promise<WriteOutcome<SessionItemV1>> =>
      settle(() => update.mutateAsync({ itemId, body }), true),
    [update.mutateAsync, settle],
  );
  const moveItem = useCallback(
    (itemId: string, body: SessionItemMoveV1): Promise<WriteOutcome<SessionItemV1>> =>
      settle(() => move.mutateAsync({ itemId, body }), false),
    [move.mutateAsync, settle],
  );
  const removeItem = useCallback(
    (itemId: string, baseRv: number): Promise<WriteOutcome<unknown>> =>
      settle(() => remove.mutateAsync({ itemId, baseRv }), false),
    [remove.mutateAsync, settle],
  );
  return { createItem, updateItem, moveItem, removeItem };
}

/** `sessions.update {status: 'prepped'}` and nothing else (no dump, pack or download). */
export function useMarkPrepped() {
  const client = useQueryClient();
  const notify = useNotify();
  const mutation = useMutation({
    mutationFn: (sessionId: string) =>
      api(
        sessionsUpdate,
        { params: { id: sessionId }, body: { status: 'prepped' } },
        undoable('sessions.update'),
      ),
  });
  const run = useCallback(
    async (sessionId: string): Promise<WriteOutcome<SessionV1>> => {
      try {
        const data = await mutation.mutateAsync(sessionId);
        await client.invalidateQueries({ queryKey: TONIGHT_KEY });
        return { ok: true, data };
      } catch (error) {
        const out = failure(error, 'write');
        if (!out.ok) notify(out.message);
        await client.invalidateQueries({ queryKey: TONIGHT_KEY });
        return out;
      }
    },
    [client, notify, mutation.mutateAsync],
  );
  return { run, pending: mutation.isPending };
}
